/**
 * QueryThreadItem.jsx
 * ─────────────────────────────────────────────────────────────
 * IJ:       IJ-254 · Phase 5E-3f · Query Thread coordinator (composition root)
 * Date:     2026-08-02
 * Umbrella: IJ-076 · Refactor Programme 2026 · Phase 5E-3
 * Owner:    E1
 *
 * Extracts the queryThread.map iteration body from KnowledgeGraphPage.jsx.
 * Composes every already-extracted sub-component (ForecastResultCard ·
 * WebSourcesBlock · AnswerContentBlock · AnswerFeedbackPanel ·
 * ProductSearchResults · SourcesSection) plus small in-line pieces
 * (quota hint · CC hint · QueryInputRow IIFE · Related-Images block ·
 * Clear-this-Search button · SuggestionsPanel wiring · back-to-bottom).
 *
 * MG-02 C1 EXCEPTION · justified · composition-root pattern · 95 per cent
 * of the body is thin passthrough on six sub-component calls. Every
 * remaining in-line region is too small to warrant a separate file.
 * Byte-verbatim on every className · style · data-testid · axios URL.
 * Zero behaviour change.
 */
import React from 'react';
import { ChevronDown, LayoutGrid, List } from 'lucide-react';
import { QueryInputRow } from './QueryInputRow';
import { QuotaDemoBox } from './QuotaDemoBox';
import { SourcesSection } from './SourcesSection';
import { SuggestionsPanel } from './SuggestionsPanel';
import { ForecastResultCard } from './ForecastResultCard';
import { WebSourcesBlock } from './WebSourcesBlock';
import { AnswerContentBlock } from './AnswerContentBlock';
import { AnswerFeedbackPanel } from './AnswerFeedbackPanel';
import { ProductSearchResults } from './ProductSearchResults';

export function QueryThreadItem({
  item, idx, isMobile, isAdmin, SHOW_DEMO_COUNTER, BACKEND_URL,
  threadEndRef, followUpRef, billedCount, demoViewed, isQuerying,
  markControlCentreVisited, fetchDataStats,
  query, setQuery, queryThread, setQueryThread, setFollowUpQuery,
  setShowDemoIntro, setShowControlCentre, setControlCentreView,
  selectedFile, includeFormulas, setIncludeFormulas,
  isForecastLoading, forecastResult, setForecastResult,
  forecastTooltip, setForecastTooltip,
  showDownloadCoachMark, setShowDownloadCoachMark, downloadForecastBtnAnchorRef,
  feedbackByQid, commentDraft, setCommentDraft, submitAnswerFeedback,
  browseMode, setBrowseMode, webViewMode, setWebViewMode,
  webResultsExpanded, setWebResultsExpanded,
  sourcesViewMode, setSourcesViewMode,
  productResultsExpanded, setProductResultsExpanded,
  mediaTab, setMediaTab, mediaCache, setMediaCache,
  mediaLoading, setMediaLoading, mediaError, setMediaError, switchMediaTab,
  setSafeBrowserState, setLightbox,
  toggleRefineOption, advanceRefinePrompt, submitRefinedQuery, skipRefinePrompt,
  handleQuery, handleCitationClick, handleDownloadForecast,
  handleDownloadSourceDocument, handleExportToWord, handleGenerateForecast,
  handleHideTooltip, handleInlineForecastUpload, handlePermissionResponse,
  handleProductSearch, markdownComponents, renderContentWithCitations,
}) {
  return (
            <div key={idx} className={`mt-8 ${isMobile ? 'px-2' : 'px-6'}`} data-testid={`thread-item-${idx}`}>
              {/* SPD v5.86 (2026-02-14) · operator directive — the small
                  "Q:" bubble that used to live here is retired. Every
                  historical query now displays via the shared
                  QueryInputRow component in readonly mode, so it looks
                  identical to the primary input at the top of the page
                  and to the active empty input at the bottom of the
                  thread. Same font size, same magnifier icon, same
                  spacing. One source of truth for what a query row
                  looks like. */}
              {(() => {
                let displayQuery = item.query;
                if (item.result?.type === 'product_search' && Array.isArray(item.result.selectedSources)) {
                  const srcs = item.result.selectedSources;
                  const hasLocal = srcs.includes('local');
                  const hasOnline = srcs.includes('online');
                  const isCompare = srcs.includes('compare') || (hasLocal && hasOnline);
                  let suffix = '';
                  if (isCompare) suffix = ' — Compare local vs big players';
                  else if (hasLocal) suffix = ' — Local only';
                  else if (hasOnline) suffix = ' — Big players only';
                  displayQuery = `${item.query}${suffix}`;
                }
                const FACET_LABELS = {
                  time_scope: 'Time scope',
                  source_type: 'Source type',
                  geography: 'Geography',
                  audience: 'Audience',
                  depth: 'Depth',
                };
                if (item.refinement_facets && Object.keys(item.refinement_facets).length > 0) {
                  const parts = Object.entries(item.refinement_facets)
                    .map(([k, v]) => {
                      const label = FACET_LABELS[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                      const vals = Array.isArray(v) ? v.filter(Boolean).join(', ') : String(v);
                      return vals ? `${label}: ${vals}` : null;
                    })
                    .filter(Boolean);
                  if (parts.length > 0) {
                    displayQuery = `${displayQuery} — ${parts.join(' · ')}`;
                  }
                }
                return (
                  <QueryInputRow
                    value={displayQuery}
                    isReadOnly
                    dataTestId={`query-bubble-${idx}`}
                  />
                );
              })()}

              {/* Quota + CC hint — shown under the first query's bubble so the
                  user always sees their remaining quota and the Control
                  Centre nudge even after leaving the empty home state. */}
              {idx === 0 && SHOW_DEMO_COUNTER && (() => {
                // Sticky billed counter survives thread replacement
                // (e.g. running the demo) so the user always sees their
                // accurate quota usage.
                const used = Math.min(billedCount, 3);
                return (
                  <QuotaDemoBox
                    used={used}
                    quota={3}
                    demoViewed={demoViewed}
                    onOpenDemo={() => setShowDemoIntro(true)}
                    showCCHint={false}
                    onOpenCC={() => { markControlCentreVisited(); setShowControlCentre(true); setControlCentreView('main'); fetchDataStats(); }}
                    onDismissCC={() => handleHideTooltip('cc_first_query')}
                    isMobile={isMobile}
                    variant="inline"
                    testIdSuffix="under-query"
                  />
                );
              })()}

              {/* D1.0 (Feb 23, 2026) — Skip the results container, Back-to-Top,
                  SuggestionsPanel, and bottom QuotaDemoBox for RESOLVED
                  clarification thread items. The grey clarification card
                  adds no value once the user has picked chips because the
                  revised query bubble below already shows the merged
                  clarification (Time Scope / etc.). The Q: bubble for the
                  original query and the top QuotaDemoBox stay so the user
                  still has the counter + their original wording visible. */}
              {!(item.result.type === 'clarification_prompt' && item.result.resolved) && (
              <>
              {/* Results container */}
              <div className={`relative rounded-xl ${isMobile ? 'p-3' : 'p-6'} mx-auto`} style={{ maxWidth: isMobile ? '100%' : '85%', backgroundColor: 'rgba(30, 35, 45, 0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)', minHeight: item.isStreaming && (item.result?.type === 'product_search' || item.result?.type === 'general') ? '240px' : undefined }} data-testid={`result-container-${idx}`}>
                {/* SPD v5.86 hotfix (2026-02-14) · operator UX critique:
                    the grey result container looked accidentally empty
                    during loading because the progressMessage was
                    rendered in tiny italic cyan text in the top-left
                    corner. Operator preference (Option 2 in their
                    playback): centre the message vertically + horizontally,
                    black high-contrast font, much bigger, so the grey
                    container reads as an intentional loading canvas
                    rather than an oversized empty box. Overlay uses
                    `absolute inset-0` on the container's relative
                    positioning so it sits on top of any (empty) child
                    sections that would otherwise render.

                    2026-02-14 second pass — extended the overlay
                    condition to `type === 'general'` too, so the
                    informational (KG / web search) loading state gets
                    the same big-centered-black treatment as
                    product_search. Was inconsistent before: product
                    queries had the big treatment, informational
                    queries were still small-italic-in-the-corner. */}
                {item.isStreaming && (item.result?.type === 'product_search' || item.result?.type === 'general') && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 rounded-xl z-10 px-6" style={{ backgroundColor: 'rgb(30, 35, 45)' }} data-testid={`product-loading-overlay-${idx}`}>
                    <span className="inline-block h-8 w-8 rounded-full border-[3px] border-cyan-300/70 border-t-transparent animate-spin" aria-hidden="true" />
                    <span className={`text-slate-100 font-semibold text-center ${isMobile ? 'text-xl' : 'text-3xl'} leading-snug`}>
                      {item.result.progressMessage || 'Searching the web'}&hellip;
                    </span>
                  </div>
                )}
                {/* D2 (Jun 11 2026) — the floating bottom-right ↑ used to live
                    here as an absolute-positioned button, which collided with
                    the Export-to-Word icon at the bottom of the card. Moved
                    into the footer-flex below so both sit side-by-side. */}
                {/* Session 3.6 — Inline Clarification Prompt (refinement chips in-thread).
                    Replaces the previous modal overlay. Rendered as a compact
                    card directly below the user's query bubble. */}
                {item.result.type === 'clarification_prompt' ? (
                  <div className="space-y-3" data-testid={`clarification-prompt-${idx}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-cyan-500/15 border border-cyan-500/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3.5 h-3.5 text-cyan-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093M12 17h.01" /></svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-100 mb-3">Clarification prompt</p>

                        {item.result.resolved ? (
                          <p className="text-xs italic text-gray-500" data-testid={`clarification-resolved-${idx}`}>
                            {item.result.skipped ? '— searched as typed' : '— applied below'}
                          </p>
                        ) : (
                          <>
                            {/* D4 (Jun 15 2026) — Multi-round, one chip group at a time.
                                Render only the active facet; "Continue" advances; last → "Search". */}
                            {(() => {
                              const allFacets = item.result.facets || [];
                              const activeIdx = Math.min(item.result.active_facet_index || 0, Math.max(allFacets.length - 1, 0));
                              const facet = allFacets[activeIdx];
                              if (!facet) return null;
                              const isTimeScope = facet.key === 'time_scope';
                              const otherOpt = (facet.options || []).find(o => o.value === 'other');
                              const otherSelected = otherOpt && (item.result.selections?.[facet.key] || []).includes(otherOpt.label);
                              const customValue = item.result.custom_time_scope || '';
                              // D4 — copy per operator (Msg 227, 15 Jun 2026).
                              // time_scope gets the bespoke "Do you want any evidenced research to go back:" framing;
                              // other facets fall back to the facet's catalog label (e.g., "Which jurisdiction?").
                              const promptText = isTimeScope
                                ? 'Do you want any evidenced research to go back:'
                                : (facet.label || 'Did you want');
                              const hasMore = activeIdx < allFacets.length - 1;
                              return (
                              <div key={facet.key} className="mb-4" data-testid={`clarification-facet-${idx}-${facet.key}`}>
                                <p className="text-sm text-gray-200 mb-2" data-testid={`clarification-prompt-text-${idx}-${facet.key}`}>
                                  {promptText}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {facet.options.map((opt) => {
                                    const selected = (item.result.selections?.[facet.key] || []).includes(opt.label);
                                    return (
                                      <button
                                        key={opt.value}
                                        onClick={() => toggleRefineOption(idx, facet, opt.label)}
                                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                                          selected
                                            ? 'bg-cyan-600 border-cyan-600 text-white'
                                            : 'bg-white/10 border-white/20 text-gray-200 hover:border-cyan-500 hover:bg-white/20'
                                        }`}
                                        data-testid={`clarification-chip-${idx}-${facet.key}-${opt.value}`}
                                      >
                                        {opt.label}
                                      </button>
                                    );
                                  })}
                                </div>
                                {isTimeScope && otherSelected && (
                                  <div className="mt-3" data-testid={`clarification-custom-${idx}-time_scope-wrapper`}>
                                    <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1.5">
                                      Enter a time scale you would like me to use:
                                    </label>
                                    <input
                                      type="text"
                                      value={customValue}
                                      onChange={(e) => setQueryThread(prev => prev.map((it, i) => i === idx
                                        ? { ...it, result: { ...it.result, custom_time_scope: e.target.value } }
                                        : it))}
                                      placeholder="e.g., Last 3 years, Since 2020, Jan 2023 to today"
                                      className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-gray-100 text-sm placeholder-gray-400 focus:outline-none focus:border-cyan-500"
                                      data-testid={`clarification-custom-${idx}-time_scope-input`}
                                    />
                                  </div>
                                )}
                                {allFacets.length > 1 && (
                                  <p className="mt-3 text-xs text-gray-500" data-testid={`clarification-progress-${idx}`}>
                                    Step {activeIdx + 1} of {allFacets.length}
                                  </p>
                                )}

                                <div className="flex items-center gap-2 mt-4">
                                  <button
                                    onClick={() => hasMore ? advanceRefinePrompt(idx) : submitRefinedQuery(idx)}
                                    className="px-4 py-1.5 rounded-lg text-sm font-semibold bg-cyan-600 hover:bg-cyan-500 text-white transition-colors"
                                    data-testid={hasMore ? `clarification-continue-${idx}` : `clarification-submit-${idx}`}
                                  >
                                    {hasMore ? 'Continue' : 'Search'}
                                  </button>
                                  <button
                                    onClick={() => skipRefinePrompt(idx)}
                                    className="px-3 py-1.5 rounded-lg text-xs text-gray-400 hover:text-white border border-white/20 hover:border-gray-500 transition-colors"
                                    data-testid={`clarification-skip-${idx}`}
                                  >
                                    Skip · search as-is
                                  </button>
                                </div>
                              </div>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ) : item.result.type === 'permission_required' ? (
                  <div className="space-y-4" data-testid={`permission-prompt-${idx}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-100 mb-2">Agent Clarification</p>
                        <div className="text-sm text-gray-200 leading-relaxed whitespace-pre-line">
                          {item.result.answer.split('**').map((part, i) => 
                            i % 2 === 1 ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 pt-2 border-t border-gray-400/30">
                      <button
                        onClick={() => handlePermissionResponse('enable', item.result.permissions_needed, item.result.original_query, item.result.kg_relevant)}
                        className="px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:scale-[1.02]"
                        style={{ backgroundColor: 'rgba(6, 182, 212, 0.85)' }}
                        data-testid={`perm-enable-btn-${idx}`}
                      >
                        Turn On & Search
                      </button>
                      <button
                        onClick={() => handlePermissionResponse('skip', item.result.permissions_needed, item.result.original_query, item.result.kg_relevant)}
                        className="px-5 py-2 rounded-lg text-sm font-medium text-gray-200 border border-white/20 hover:bg-white/10 transition-all"
                        data-testid={`perm-skip-btn-${idx}`}
                      >
                        Search Without
                      </button>
                    </div>
                  </div>
                ) : (
                <>
                {/* Version label for briefs */}
                {item.result.version && (
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-400/30" data-testid={`version-label-${idx}`}>
                    <span className="px-3 py-1 rounded-full text-sm font-bold tracking-wide shadow-sm" style={{ backgroundColor: '#0891b2', color: '#FFFFFF', border: '1px solid #0e7490' }}>
                      Version {item.result.version}
                    </span>
                    {item.result.version !== 'V1.0' && (
                      <span className="text-xs text-gray-600">Updated from previous version</span>
                    )}
                  </div>
                )}

                {/* ════════════════════════════════════════════════════════════════
                    WEB RESULTS — extracted to components/app/WebSourcesBlock.jsx in Phase 5E-3b (IJ-250)
                    ════════════════════════════════════════════════════════════════ */}
                <WebSourcesBlock
                  item={item}
                  idx={idx}
                  isMobile={isMobile}
                  browseMode={browseMode}
                  setBrowseMode={setBrowseMode}
                  webViewMode={webViewMode}
                  setWebViewMode={setWebViewMode}
                  webResultsExpanded={webResultsExpanded}
                  setWebResultsExpanded={setWebResultsExpanded}
                  mediaTab={mediaTab}
                  mediaCache={mediaCache}
                  mediaLoading={mediaLoading}
                  mediaError={mediaError}
                  switchMediaTab={switchMediaTab}
                  setSafeBrowserState={setSafeBrowserState}
                  setLightbox={setLightbox}
                  handleQuery={handleQuery}
                />

                {/* ════════════════════════════════════════════════════════════════
                    PRODUCT SEARCH RESULTS — extracted to components/app/ProductSearchResults.jsx in Phase 5E-3e (IJ-253)
                    ════════════════════════════════════════════════════════════════ */}
                <ProductSearchResults
                  item={item}
                  idx={idx}
                  isMobile={isMobile}
                  browseMode={browseMode}
                  setBrowseMode={setBrowseMode}
                  sourcesViewMode={sourcesViewMode}
                  setSourcesViewMode={setSourcesViewMode}
                  productResultsExpanded={productResultsExpanded}
                  setProductResultsExpanded={setProductResultsExpanded}
                  setSafeBrowserState={setSafeBrowserState}
                  handleProductSearch={handleProductSearch}
                  markdownComponents={markdownComponents}
                  renderContentWithCitations={renderContentWithCitations}
                />

                {/* SOURCES SECTION - Extracted Component */}
                {item.result.source_documents && item.result.source_documents.length > 0 && item.result.type !== 'product_search' && (
                  <SourcesSection
                    sourceDocuments={item.result.source_documents}
                    sourcesViewMode={sourcesViewMode}
                    setSourcesViewMode={setSourcesViewMode}
                    onDownloadSource={handleDownloadSourceDocument}
                    idx={idx}
                  />
                )}

                {/* Related Images - Also shown ABOVE synthesized answer if present */}
                {item.result.images && item.result.images.length > 0 && (
                  <div className="mb-5 pb-4 border-b border-gray-400/30">
                    {/* Header with toggle */}
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-white text-sm">Related Images ({item.result.images.length})</h4>
                      {/* View toggle for images */}
                      <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
                        <button
                          onClick={() => setSourcesViewMode('list')}
                          className={`p-1.5 rounded transition-colors ${sourcesViewMode === 'list' ? 'bg-white/15 shadow-sm text-cyan-300' : 'text-gray-400 hover:text-white'}`}
                          title="List view"
                        >
                          <List className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setSourcesViewMode('circles')}
                          className={`p-1.5 rounded transition-colors ${sourcesViewMode === 'circles' ? 'bg-white/15 shadow-sm text-cyan-300' : 'text-gray-400 hover:text-white'}`}
                          title="Circle view"
                        >
                          <LayoutGrid className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* List View for Images */}
                    {sourcesViewMode === 'list' && (
                      <div className="space-y-4">
                        {item.result.images.map((image, imgIdx) => (
                          <div key={imgIdx} className="bg-white/10 rounded-lg p-3">
                            <img src={`${BACKEND_URL}${image.image_url}`} alt={image.description} className="max-w-full h-auto rounded-lg shadow-md mb-2" style={{ maxHeight: '500px', objectFit: 'contain' }} />
                            <p className="text-sm text-gray-300 mt-2">{image.description}{image.slide_number > 0 && ` (Slide ${image.slide_number})`}{image.page_number > 0 && ` (Page ${image.page_number})`}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Circle View for Images */}
                    {/* Circle View for Images - 5 per row, fill width */}
                    {sourcesViewMode === 'circles' && (
                      <div className="pb-2">
                        <div className="flex justify-between pr-2">
                          {item.result.images.slice(0, 5).map((image, imgIdx) => (
                            <div 
                              key={imgIdx} 
                              className="flex flex-col items-center gap-3 cursor-pointer group"
                              style={{ width: '18%' }}
                            >
                              <div 
                                className="w-48 h-48 rounded-full overflow-hidden shadow-lg transition-all duration-300 ease-out group-hover:scale-150 group-hover:shadow-2xl group-hover:z-10"
                              >
                                <img 
                                  src={`${BACKEND_URL}${image.image_url}`} 
                                  alt={image.description} 
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <p className="text-center font-bold leading-tight line-clamp-2 px-1" style={{ color: '#ffffff', fontSize: '1rem' }}>
                                {image.description ? (image.description.length > 20 ? image.description.substring(0, 18) + '...' : image.description) : `Image ${imgIdx + 1}`}
                              </p>
                            </div>
                          ))}
                        </div>
                        {item.result.images.length > 5 && (
                          <p className="text-xs text-gray-500 mt-2">+{item.result.images.length - 5} more images</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ════════════════════════════════════════════════════════════════
                    SYNTHESIZED ANSWER - Now displayed AFTER sources
                    ════════════════════════════════════════════════════════════════ */}
                {/* ════════════════════════════════════════════════════════════════
                    ANSWER CONTENT — extracted to components/app/AnswerContentBlock.jsx in Phase 5E-3c (IJ-251)
                    Six sub-regions: prose wrapper (progress + answer) · sources footnote ·
                    recovery card · export/back-to-top row.
                    ════════════════════════════════════════════════════════════════ */}
                <AnswerContentBlock
                  item={item}
                  idx={idx}
                  isMobile={isMobile}
                  handleCitationClick={handleCitationClick}
                  handleExportToWord={handleExportToWord}
                  handleQuery={handleQuery}
                  markdownComponents={markdownComponents}
                  renderContentWithCitations={renderContentWithCitations}
                />
                </>
                )}
              </div>

              {/* Forecast Upload Controls - appears BELOW the grey box.
                  D2-#2 · SPD v5.76 (2026-07-12) — Beta scope-clamp fix.
                  The entire upload block is now admin-only. Non-admin beta
                  users see NO controls (no button, no checkbox, no
                  Generate). Backend already rejects the upload/generate
                  calls for non-admins; this closes the UX gap so a beta
                  user never gets the file picker to open in the first
                  place. Consistent with the KG document-upload gate. */}
              {/* Forecast section — renders for BOTH admin (custom Excel
                  uploads) and non-admin (P&L Demo view). Extracted to
                  components/app/ForecastResultCard.jsx in Phase 5E-3a
                  (IJ-249). The `isAdmin` guard is preserved inside the
                  component and still narrows to just the upload toolbar
                  so beta users can still see the demo dashboard they
                  triggered from the CTA. D8 fix · 2026-02-14. */}
              <ForecastResultCard
                item={item}
                isAdmin={isAdmin}
                selectedFile={selectedFile}
                includeFormulas={includeFormulas}
                setIncludeFormulas={setIncludeFormulas}
                isForecastLoading={isForecastLoading}
                forecastResult={forecastResult}
                forecastTooltip={forecastTooltip}
                setForecastTooltip={setForecastTooltip}
                setForecastResult={setForecastResult}
                showDownloadCoachMark={showDownloadCoachMark}
                setShowDownloadCoachMark={setShowDownloadCoachMark}
                downloadForecastBtnAnchorRef={downloadForecastBtnAnchorRef}
                handleGenerateForecast={handleGenerateForecast}
                handleInlineForecastUpload={handleInlineForecastUpload}
                handleDownloadForecast={handleDownloadForecast}
              />

              {/* ════════════════════════════════════════════════════════════════
                  ANSWER FEEDBACK — extracted to components/app/AnswerFeedbackPanel.jsx in Phase 5E-3d (IJ-252)
                  Thumbs up/down · rating-recorded badge · root-cause dropdown · comment draft textarea.
                  Guard: item.query_id present AND item.result.is_demo falsy.
                  ════════════════════════════════════════════════════════════════ */}
              <AnswerFeedbackPanel
                item={item}
                idx={idx}
                feedbackByQid={feedbackByQid}
                commentDraft={commentDraft}
                setCommentDraft={setCommentDraft}
                submitAnswerFeedback={submitAnswerFeedback}
              />

              {/* D10.0 (Feb 24, 2026) — "Clear this Search" promoted from inside
                  the SuggestionsPanel to a top-level right-aligned button.
                  Renders for EVERY thread item (including the Demo, which
                  has no SuggestionsPanel) so users can always remove the
                  current result from the thread. The Back-to-Top button
                  that used to sit here is now a floating ↑ icon at the
                  bottom-right of the result card above. */}
              <div className="flex justify-end mt-3 mx-auto" style={{ maxWidth: '85%' }}>
                <button
                  onClick={() => { setQueryThread([]); setQuery(''); setFollowUpQuery(''); setMediaCache({}); setMediaTab({}); setMediaLoading({}); setMediaError({}); }}
                  className="text-cyan-400 hover:text-cyan-300 text-sm transition-colors px-3 py-1.5 rounded hover:bg-cyan-400/10"
                  data-testid={`clear-search-${idx}`}
                >
                  Clear this Search
                </button>
              </div>

              {/* AI Suggestions - Extracted Component */}
              <SuggestionsPanel
                suggestions={item.result.suggestions}
                onSuggestionClick={(suggestion) => {
                  // SPD v5.87.2 hotfix (2026-02-14) — operator regression:
                  // clicking a follow-up suggestion left the suggestion
                  // text in the follow-up input, producing a duplicate
                  // query heading below the grey searching-knowledge-graph
                  // loading box. SuggestionsPanel populates the input via
                  // setFollowUpQuery(suggestion) then fires this handler
                  // 300 ms later — we now clear synchronously so the
                  // input goes empty the moment the search fires.
                  setFollowUpQuery('');
                  handleQuery(suggestion, true);
                }}
                onClearSearch={() => { setQueryThread([]); setQuery(''); setFollowUpQuery(''); setMediaCache({}); setMediaTab({}); setMediaLoading({}); setMediaError({}); }}
                isQuerying={isQuerying}
                followUpRef={followUpRef}
                setFollowUpQuery={setFollowUpQuery}
                idx={idx}
              />

              {/* D3.0 (Feb 23, 2026) — Anchor the bottom QuotaDemoBox to the
                  LAST thread item so it bookends the visible thread instead
                  of the first item. Previously gated on idx===0; when a
                  clarification fired as item 0 (which has no Suggestions
                  panel), the bottom counter never rendered. Now follows the
                  most recent results card. */}
              {idx === queryThread.length - 1 && SHOW_DEMO_COUNTER && (() => {
                const used = Math.min(billedCount, 3);
                return (
                  <QuotaDemoBox
                    used={used}
                    quota={3}
                    demoViewed={demoViewed}
                    onOpenDemo={() => setShowDemoIntro(true)}
                    showCCHint={false}
                    onOpenCC={() => { markControlCentreVisited(); setShowControlCentre(true); setControlCentreView('main'); fetchDataStats(); }}
                    onDismissCC={() => handleHideTooltip('cc_first_query')}
                    isMobile={isMobile}
                    variant="inline"
                    testIdSuffix="under-suggestions"
                  />
                );
              })()}
              </>
              )}

              {/* IJ-493 (2026-09-03) — the mid-thread "Back to bottom" pill was
                  replaced by a single fixed floating arrow rendered once in
                  KnowledgeGraphPage (see components/app/BackToBottomButton.jsx). */}
            </div>
  );
}
